<template>
  <loading v-if="isLoading" message="Logging In" />
  <main
    class="w-screen h-screen flex flex-col lg:flex-row p-0 lg:p-4 gap-0 lg:gap-4 bg-white dark:bg-gray-900 transition-colors duration-300">
    <!-- Orange Image -->
    <div
      class="w-full lg:w-1/2 h-screen lg:h-full bg-[#E87055] dark:bg-gray-800/80 relative flex flex-col items-center justify-center text-white p-0 lg:p-8 rounded-none lg:rounded-lg transition-colors duration-300"
      :style="{
        backgroundImage: `url(${worldMap})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        filter: $store.darkMode ? 'grayscale(40%) brightness(0.8)' : 'none',
        fontFamily: `'Syne', sans-serif`,
      }">
      <!-- Version Text -->
      <div class="absolute right-4 bottom-2 text-white text-sm sm:text-base font-semibold">
        <version />
      </div>

      <!-- Logo Header -->
      <div
        class="absolute top-0 left-0 w-full sm:w-full lg:w-auto bg-white/50 dark:bg-white/50 backdrop-blur-md flex items-center justify-center shadow-md p-2 sm:p-4 rounded lg:top-4 lg:left-4 lg:p-4 transition-colors duration-300">
        <img src="../assets/logo-header.png" alt="Logo"
          class="w-80 h-16 sm:w-64 sm:h-20 md:w-72 md:h-24 lg:w-64 lg:h-16 object-contain" />
      </div>

      <!-- Portal Text -->
      <div
        class="flex text-2xl flex-row sm:flex-row md:justify-center md:gap-4 text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-center lg:text-left lg:flex-col mt-8 sm:mt-12 md:mt-16 lg:mt-0 gap-2 text-white dark:text-gray-100 transition-colors duration-300 bg-white/10 dark:bg-gray-900/30 backdrop-blur-sm px-6 py-4 rounded">
        <span>Service</span>
        <span>Invoice</span>
        <span>Portal</span>
      </div>

      <!-- User Login for small screens -->
      <div
        class="w-full max-w-xs bg-white/30 dark:bg-gray-800/40 backdrop-blur-md border border-white/20 dark:border-gray-600 rounded-lg p-6 shadow-lg mt-8 sm:mt-12 lg:hidden transition-colors duration-300">
        <h2 class="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-200">
          User Login
        </h2>
        <form @submit.prevent="loginUser"
          class="flex flex-col gap-4 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xs mx-auto">
          <p v-if="errorMessage" class="text-sm text-red-500 font-medium">
            {{ errorMessage }}
          </p>
          <div class="flex flex-col">
            <label for="username" class="mb-2 font-semibold text-gray-700 dark:text-gray-300">Username:</label>
            <input v-model="userPayload.username" type="text" id="username" placeholder="Enter your username"
              class="p-3 rounded-none border-l-2 border-white/30 dark:border-gray-600 bg-white/50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-700 dark:placeholder-gray-400 backdrop-blur-md focus:border-black focus:outline-none focus:ring-0 transition-colors duration-300" />
          </div>

          <div class="flex flex-col">
            <label for="password" class="mb-2 font-semibold text-gray-700 dark:text-gray-300">Password:</label>
            <input v-model="userPayload.password" type="password" id="password" placeholder="Enter your password"
              class="p-3 rounded-none border-l-2 border-white/30 dark:border-gray-600 bg-white/50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-700 dark:placeholder-gray-400 backdrop-blur-md focus:border-black focus:outline-none focus:ring-0 transition-colors duration-300" />
          </div>

          <button type="submit"
            class="bg-brand_orange3 hover:bg-brand_orange2 text-white font-bold py-3 px-6 rounded mt-4">
            Login
          </button>
        </form>
      </div>
    </div>

    <!-- Right Side Login for large screens only -->
    <div
      class="hidden lg:flex flex-1 bg-white dark:bg-gray-900 rounded-lg h-auto lg:h-full p-8 flex-col justify-center items-center transition-colors duration-300"
      :style="{ fontFamily: `'Syne', sans-serif` }">
      <div class="w-full max-w-xs flex flex-col gap-8 mx-auto">
        <p v-if="errorMessage" class="text-m text-red-500 font-medium">
          {{ errorMessage }}
        </p>
        <h2 class="self-start text-sm sm:text-2xl md:text-3xl lg:text-3xl font-bold text-gray-800 dark:text-gray-200">
          User Login
        </h2>

        <form @submit.prevent="loginUser" class="flex flex-col gap-4 w-full">
          <div class="flex flex-col">
            <label for="username" class="mb-2 font-semibold text-gray-700 dark:text-gray-300">Username:</label>
            <input v-model="userPayload.username" type="text" id="username" placeholder="Enter your username"
              class="p-3 rounded-none bg-white dark:bg-gray-700 text-black dark:text-gray-100 border-l-2 border-gray-400 dark:border-gray-600 focus:border-black focus:outline-none focus:ring-0 transition-colors duration-300" />
          </div>

          <div class="flex flex-col">
            <label for="password" class="mb-2 font-semibold text-gray-700 dark:text-gray-300">Password:</label>
            <input v-model="userPayload.password" type="password" id="password" placeholder="Enter your password"
              class="p-3 rounded-none bg-white dark:bg-gray-700 text-black dark:text-gray-100 border-l-2 border-gray-400 dark:border-gray-600 focus:border-black focus:outline-none focus:ring-0 transition-colors duration-300" />
          </div>

          <button type="submit"
            class="bg-brand_orange3 hover:bg-brand_orange2 text-white font-bold py-3 px-6 rounded mt-6">
            Login
          </button>
        </form>
      </div>
    </div>
  </main>
</template>

<script>
import { inject, reactive, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import worldMap from "../assets/WorldMap-orange.png";
import version from "@/components/common/version.vue";
import loading from "@/components/modals/loading.vue";

export default {
  components: { version, loading },
  setup() {
    const axios = inject("$axios");
    const router = useRouter();

    const isLoading = ref(false);
    const errorMessage = ref();

    const userPayload = reactive({
      username: null,
      password: null,
    });

    const loginUser = async () => {

      isLoading.value = true;
      errorMessage.value = "";

      try {
        const res = await axios.post("/api/user/login", userPayload);

        console.log("LOGIN RESPONSE:", res.data);

        const token = res.data?.data?.token;

        if (!token) {
          throw new Error("Token not received from server");
        }

        localStorage.setItem("token", token);

        router.push("/serviceInvoice/List");

      } catch (err) {
        console.error(err);

        errorMessage.value =
          err.response?.data?.message ||
          "Login failed! Please try again.";

      } finally {
        isLoading.value = false;
      }
    };

    // Check token on mount
    onMounted(() => {
      const token = localStorage.getItem("token");
      if (token) {
        // Redirect if token exists
        router.push("/serviceInvoice/List");
      }
    });

    return { userPayload, errorMessage, worldMap, isLoading, loginUser };
  },
};
</script>
